import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Clock, Info, Lock } from 'lucide-react';
import { FeeLines } from './OrderSlotSummary';
import { TIER_LABELS } from './DeliverySlotRow';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { deliveryAddressToWire } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => (Number(n) > 0 ? `$${Number(n).toFixed(2)}` : 'Free');

export function useDeliverySlots(enabled, address) {
  const wire = deliveryAddressToWire(address);
  const [state, setState] = useState({ loading: enabled, days: [], longDistance: false, tier: null });
  const [slot, setSlot] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/delivery-slots/available`, { params: { address: wire }, withCredentials: true });
        setState({ loading: false, days: data.days || [], longDistance: !!data.long_distance, tier: data.tier || null });
      } catch {
        setState({ loading: false, days: [], longDistance: false, tier: null });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [enabled, wire]);

  // Re-resolve the selection against fresh availability so the fee stays current.
  useEffect(() => {
    setSlot((cur) => {
      if (!cur) return cur;
      const day = state.days.find((d) => d.date === cur.date);
      const s = day?.slots.find((x) => x.hour === cur.hour && !x.locked);
      return s ? { ...s, date: day.date, day_label: day.day_label } : null;
    });
  }, [state.days]);

  return { ...state, slot, setSlot, fee: enabled && slot ? Number(slot.fee) : 0, none: enabled && !state.loading && state.days.length === 0 };
}

const optText = (s) => {
  const named = s.label && s.label !== s.range;
  return `${named ? `${s.label} · ` : ''}${s.range} · ${money(s.fee)}`;
};

const FEE_INFO = 'Delivery fees vary by time slot. Your tier may qualify you for a discount or free delivery.';
const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

export const rushTagText = (label) => (label || 'Rush hour fee').replace(/\s+fee$/i, '') || 'Rush hour';

function RushTag({ on, modern, hour, label }) {
  if (!on) return null;
  return (
    <span className={`ml-2 align-middle inline-block whitespace-nowrap text-[10px] font-semibold px-1.5 py-0.5 ${modern ? 'rounded-full' : 'font-mono border border-[#FF9F0A]/70'}`}
      style={modern ? { background: '#FEF3C7', color: '#92400E' } : { color: '#FF9F0A' }}
      data-testid={`slot-rush-tag-${hour}`}>
      {rushTagText(label)}
    </span>
  );
}

// (i) next to each option's fee; tap/hover shows a floating bubble without selecting the slot.
function FeeInfo({ hour, modern }) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen} delayDuration={100}>
      <TooltipTrigger asChild>
        <span role="button" tabIndex={-1} aria-label="About delivery fees"
          onPointerDown={stop} onPointerUp={stop} onClick={(e) => { stop(e); setOpen((o) => !o); }}
          className={`inline-flex items-center ml-1.5 align-middle cursor-help ${modern ? 'text-[#8E8983] hover:text-[#047857]' : 'text-[#6B8E73] hover:text-[#39FF14]'}`}
          data-testid={`slot-fee-info-${hour}`}>
          <Info className="w-3.5 h-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" collisionPadding={12}
        className={`z-[70] max-w-[240px] text-xs leading-snug pointer-events-none ${modern
          ? 'bg-white text-[#3A3934] border border-[#EFEDE7] rounded-xl shadow-lg px-3 py-2'
          : 'bg-[#0C0612] text-[#A3C7A8] border border-[#39FF14] rounded-none font-mono px-2.5 py-1.5'}`}
        data-testid={`slot-fee-tooltip-${hour}`}>
        {FEE_INFO}
      </TooltipContent>
    </Tooltip>
  );
}

export function DeliverySlotPicker({ quote, modern }) {
  const { days, loading, slot, setSlot, none } = quote;
  const [date, setDate] = useState(null);
  const activeDate = date && days.some((d) => d.date === date) ? date : days[0]?.date;
  const day = useMemo(() => days.find((d) => d.date === activeDate), [days, activeDate]);

  const label = modern
    ? { fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--am-ink-3)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }
    : undefined;
  const chip = (on) => (modern
    ? { padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', border: `1px solid ${on ? 'var(--am-emerald-700)' : 'var(--am-hair)'}`, background: on ? 'var(--am-emerald-50, #ECFDF5)' : 'var(--am-card)', color: on ? 'var(--am-emerald-700)' : 'var(--am-ink-2)' }
    : undefined);
  const chipCls = (on) => (modern ? '' : `px-3 py-1.5 border font-mono text-xs whitespace-nowrap transition-colors ${on ? 'border-[#39FF14] text-[#39FF14] bg-[rgba(57,255,20,0.08)]' : 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14]'}`);

  return (
    <div data-testid="delivery-slot-picker" style={{ minWidth: 0, maxWidth: "100%" }}>
      <div style={label} className={modern ? '' : 'text-[#6B8E73] font-mono text-xs uppercase tracking-wider flex items-center gap-2 mb-2'}>
        <Clock className="w-4 h-4" /> {modern ? 'Delivery time' : 'DELIVERY_SLOT'}
      </div>
      {loading ? (
        <p className={modern ? '' : 'font-mono text-xs text-[#6B8E73]'} style={modern ? { fontSize: 13, color: 'var(--am-ink-3)' } : undefined} data-testid="slot-loading">Loading delivery times…</p>
      ) : none ? (
        <p className={modern ? '' : 'p-2.5 border border-[#FF3B30] text-[#FF3B30] font-mono text-xs'}
           style={modern ? { padding: 10, borderRadius: 'var(--am-radius-sm)', border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#B91C1C', fontSize: 13 } : undefined}
           data-testid="slot-none">No delivery slots available right now</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-2" data-testid="slot-days">
            {days.map((d) => (
              <button key={d.date} type="button" onClick={() => setDate(d.date)} style={chip(d.date === activeDate)} className={chipCls(d.date === activeDate)} data-testid={`slot-day-${d.date}`}>
                {d.day_label}
              </button>
            ))}
          </div>
          <Select
            value={slot && slot.date === activeDate ? String(slot.hour) : ''}
            onValueChange={(v) => { const s = day.slots.find((x) => String(x.hour) === v); setSlot({ ...s, date: day.date, day_label: day.day_label }); }}
          >
            <SelectTrigger
              className={modern ? '' : 'bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none focus:border-[#39FF14]'}
              style={modern ? { background: 'var(--am-card)', border: '1px solid var(--am-hair)', borderRadius: 'var(--am-radius-sm)', color: 'var(--am-ink-1)' } : undefined}
              data-testid="slot-select"
            >
              <SelectValue placeholder="Choose a time window">{slot && slot.date === activeDate ? optText(slot) : null}</SelectValue>
            </SelectTrigger>
            <SelectContent className={modern ? 'bg-white text-[#111614]' : 'bg-[#0C0612] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none'}>
              <TooltipProvider>
                {day?.slots.map((s) => (s.locked ? (
                  <SelectItem key={s.hour} value={`locked-${s.hour}`} disabled data-testid={`slot-option-locked-${s.hour}`}>
                    <span className="inline-flex items-center gap-1.5 opacity-60">
                      <Lock size={12} /> {s.range} · {(s.allowed_tiers || []).map((t) => TIER_LABELS[t] || t).join(' / ')} only
                    </span>
                  </SelectItem>
                ) : (
                  <SelectItem key={s.hour} value={String(s.hour)} data-testid={`slot-option-${s.hour}`}>
                    {optText(s)}<RushTag on={s.rush_hour} modern={modern} hour={s.hour} label={s.rush_label} /><FeeInfo hour={s.hour} modern={modern} />
                  </SelectItem>
                )))}
              </TooltipProvider>
            </SelectContent>
          </Select>
          {slot && slot.date === activeDate && (
            <p className={modern ? '' : 'mt-2 font-mono text-xs text-[#39FF14]'} style={modern ? { marginTop: 8, fontSize: 12, color: 'var(--am-emerald-700)' } : undefined} data-testid="slot-selected-summary">
              {slot.day_label} · {slot.range} · Delivery {money(slot.fee)}
              <RushTag on={slot.rush_hour} modern={modern} hour="selected" label={slot.rush_label} />
            </p>
          )}
        </>
      )}
    </div>
  );
}

export const slotPayload = (on, slot) => (on && slot ? { date: slot.date, hour: slot.hour } : null);
